import dayjs from '~/timeInit';

import { VendorMonthlySummary, VendorPromotion, VendorSummaryComparison } from '~/types/services/vendor';

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('en-AU');

function formatCurrency(value: number): string {
  return CURRENCY_FORMATTER.format(value);
}

function formatNumber(value: number, decimals = 0): string {
  if (decimals > 0) {
    return value.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  return NUMBER_FORMATTER.format(Math.round(value));
}

function formatPercentChange(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

type MetricFormatter = (value: number) => string;

function avgPeriodLabel(monthsInAvg: number | null): string {
  const months = monthsInAvg ?? 12;
  return `${months} month avg`;
}

function metricBlock(
  label: string,
  comparison: VendorSummaryComparison,
  formatter: MetricFormatter,
  unitSuffix = '',
  avgSuffix = '',
): Array<string> | null {
  const withSuffix = (formatted: string) => (unitSuffix ? `${formatted} ${unitSuffix}` : formatted);
  const periodLabel = avgPeriodLabel(comparison.months_in_avg);
  const lines: Array<string> = [];

  if (comparison.monthly_avg != null) {
    const trailing = avgSuffix ? ` ${avgSuffix}` : '';
    lines.push(`* ${periodLabel}: ${withSuffix(formatter(comparison.monthly_avg))}${trailing}`);
  }
  if (comparison.last_month != null) {
    lines.push(`* last month: ${withSuffix(formatter(comparison.last_month))}`);
  }
  if (comparison.percent_change != null) {
    lines.push(`* % change vs ${periodLabel}: ${formatPercentChange(comparison.percent_change)}`);
  }

  if (lines.length === 0) {
    return null;
  }
  return [label, ...lines];
}

export type BuildEmailInput = {
  summary: VendorMonthlySummary;
  vendorName: string;
  promotions?: Array<VendorPromotion>;
};

export function buildMonthlyPerformanceEmail({ summary, vendorName, promotions = [] }: BuildEmailInput): string {
  const monthLabel = dayjs(summary.month, 'YYYY-MM').format('MMMM YYYY');
  const { performance } = summary;

  const metricBlocks = [
    metricBlock('TTV', performance.ttv, formatCurrency, '', 'per month'),
    metricBlock('Room nights', performance.room_nights, (v) => formatNumber(v), '', 'per month'),
    metricBlock('LOS', performance.los, (v) => formatNumber(v, 1), 'nights'),
    metricBlock('Lead time', performance.lead_time, (v) => formatNumber(v, 0), 'days'),
    metricBlock('Views', performance.views, (v) => formatNumber(v)),
  ].filter((block): block is Array<string> => block !== null);

  const performanceSection: Array<string> =
    metricBlocks.length > 0
      ? ['Performance Metrics', '', ...metricBlocks.flatMap((block, i) => (i === 0 ? block : ['', ...block])), '']
      : [];

  return [
    `Subject: Your monthly LE performance snapshot — ${monthLabel}`,
    '',
    'Hi team,',
    '',
    `Sharing your latest performance report from Luxury Escapes for ${vendorName} (${monthLabel}). We would love to book in a face-to-face catch up to run through the data together and align on priorities for the coming month. Please reply with a couple of times that work for you.`,
    '',
    ...performanceSection,
    ...buildPromotionsSection(promotions),
    ...buildPropertyInsightsSection(summary),
    'If anything here prompts questions, we are happy to walk through it live.',
    '',
    'Best regards,',
    '[Market Manager name]',
    'LE Partner Team',
  ].join('\n');
}

function formatPromotionDateRange(promotion: VendorPromotion): string {
  const start = dayjs(promotion.start_date).format('D MMM YYYY');
  const end = dayjs(promotion.end_date).format('D MMM YYYY');
  return `${start}-${end}`;
}

function formatPromotionDiscount(promotion: VendorPromotion): string | null {
  if (promotion.discount_amount == null) return null;
  const amount =
    typeof promotion.discount_amount === 'string' ? Number(promotion.discount_amount) : promotion.discount_amount;
  if (!Number.isFinite(amount)) return null;
  if (promotion.discount_type === 'percentage') return `Discount: ${formatNumber(amount, 0)}%`;
  if (promotion.discount_type === 'fixed') return `Discount: ${formatCurrency(amount)}`;
  return `Discount: ${formatNumber(amount, 0)} ${promotion.discount_type ?? ''}`.trim();
}

function buildPromotionsSection(promotions: Array<VendorPromotion>): Array<string> {
  if (!promotions.length) return [];
  const bullets = promotions.map((promotion) => {
    const segments: Array<string> = [];
    const discount = formatPromotionDiscount(promotion);
    if (discount) segments.push(discount);
    segments.push(formatPromotionDateRange(promotion));
    const regionCount = promotion.discount_regions?.length ?? 0;
    if (regionCount > 0) segments.push(`${regionCount} ${regionCount === 1 ? 'region' : 'regions'}`);
    return `* ${segments.join('; ')}`;
  });
  return ['Promotions', ...bullets, ''];
}

function buildPropertyInsightsSection(summary: VendorMonthlySummary): Array<string> {
  const bullets = summary.properties
    .slice(0, 5)
    .map((property) => {
      const segments: Array<string> = [];
      const impressions = property.search_impressions.last_month;
      const overpriced = property.rate_parity_overpriced_rate.last_month;

      if (impressions != null) {
        segments.push(`${formatNumber(impressions)} search impressions`);
      }
      if (overpriced != null) {
        segments.push(`${(overpriced * 100).toFixed(1)}% of impressions overpriced`);
      }

      if (segments.length === 0) {
        return null;
      }
      const name = property.property_name ?? property.offer_bk;
      return `* ${name}: ${segments.join(', ')}`;
    })
    .filter((bullet): bullet is string => bullet !== null);

  if (bullets.length === 0) {
    return [];
  }
  return ['Parity & Search insights', ...bullets, ''];
}
